// hooks/useSingleExamSession.js
import { useEffect, useRef, useState, useCallback } from "react";

/**
 * useSingleExamSession
 * -------------------
 * SINGLE/SUBJECT exam engine:
 * - Server-authoritative timer sync (no drift)
 * - Smooth local ticking based on last server sync timestamp
 * - Visibility re-sync
 * - heartbeat (15s)
 * - autosave (10s) using flushQuestionIfDirty
 * - answer selection + immediate PATCH (inflight dedupe)
 * - flag toggle + PATCH + rollback
 * - safe navigation between questions (flush prev before moving)
 *
 * Parent should:
 * - Call setters returned by this hook inside hydrateSession/loadSession.
 * - Call stopAllIntervals() in handleSubmit/unmount.
 * - Pass onAutoSubmit = () => handleSubmit(true) into this hook.
 *
 * BACKEND EXPECTATION (recommended):
 *  GET  /exam/:examId/status  -> { remaining_seconds: number }
 *  POST /exam/:examId/pause   -> optional
 *  POST /exam/:examId/resume  -> optional
 */

export default function useSingleExamSession({
  examId,
  api,
  onAutoSubmit, // parent passes () => handleSubmit(true)
}) {
  /* ====================== STATE ====================== */
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState({});
  const [currentIndex, setCurrentIndex] = useState(0);

  // Compatibility flags (children expect it)
  const [saving, setSaving] = useState(false);
  const [navBusy, setNavBusy] = useState(false);

  // This is DISPLAY remaining seconds (derived from server sync)
  const [remainingSec, setRemainingSec] = useState(0);

  // UI observables
  const [submittedUI, setSubmittedUI] = useState(false);
  const [submittingUI, setSubmittingUI] = useState(false);

  /* ====================== REFS ====================== */
  const timerRef = useRef(null); // kept for compatibility (but not used as authority)
  const autosaveRef = useRef(null);
  const heartbeatRef = useRef(null);

  // NEW: server-time sync + local ticking
  const tickRef = useRef(null); // local smooth tick (derived)
  const syncRef = useRef(null); // periodic server resync

const pausedRef = useRef(false);
// === NEW REF (very important)
const seededRef = useRef(false);
const autoPausedRef = useRef(false);


  // Parent will set these in handleSubmit flow; hook uses them for guards.
  const submittedRef = useRef(false);
  const submittingRef = useRef(false);

  const dirtyMapRef = useRef(new Map());
  const lastSavedRef = useRef(new Map());
  const inflightSaveRef = useRef(new Set());

  const currentIndexRef = useRef(0);
  const currentQIdRef = useRef(null);

  // Server sync anchors
  const serverRemainingAtSyncRef = useRef(0);
  const localSyncAtMsRef = useRef(0);

  const currentQ = questions[currentIndex];

  /* ====================== HELPERS ====================== */
  const computeStartSeconds = useCallback((examObj) => {
    if (Number.isInteger(examObj?.remaining_seconds)) {
      return Math.max(0, examObj.remaining_seconds);
    }
    if (Number.isFinite(examObj?.remaining_minutes)) {
      return Math.max(0, Math.floor(examObj.remaining_minutes * 60));
    }
    return 15 * 60;
  }, []);

  const clearIntervalSafe = (ref) => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  };

const stopAllIntervals = useCallback(() => {
  clearIntervalSafe(timerRef);
  clearIntervalSafe(tickRef);
  clearIntervalSafe(syncRef);
  clearIntervalSafe(autosaveRef);
  clearIntervalSafe(heartbeatRef);
}, []);


  /* ====================== KEEP REFS SYNCED ====================== */
  useEffect(() => {
    currentIndexRef.current = currentIndex;
    currentQIdRef.current = currentQ?.id || null;
  }, [currentIndex, currentQ?.id, currentQ]);

  /* ====================== SERVER-AUTH TIMER CORE ====================== */
  const deriveRemainingNow = useCallback(() => {
    const base = Number(serverRemainingAtSyncRef.current) || 0;
    const t0 = Number(localSyncAtMsRef.current) || 0;

    if (!t0) return Math.max(0, base);

    const elapsedSec = Math.floor((Date.now() - t0) / 1000);
    return Math.max(0, base - elapsedSec);
  }, []);

  const applyServerRemaining = useCallback((serverRemainingSeconds) => {
    const val = Math.max(0, Number(serverRemainingSeconds) || 0);

    serverRemainingAtSyncRef.current = val;
    localSyncAtMsRef.current = Date.now();

    // Update display immediately
    setRemainingSec(val);

    // Auto-submit if server says time is up
    if (
      val <= 0 &&
      !submittedRef.current &&
      !submittingRef.current &&
      typeof onAutoSubmit === "function"
    ) {
      onAutoSubmit();
    }
  }, [onAutoSubmit]);

  const syncRemainingFromServer = useCallback(async () => {
    if (!examId) return;
    if (pausedRef.current) return;
    if (submittedRef.current || submittingRef.current) return;

    try {
      // RECOMMENDED endpoint
const res = await api.get(`/exam/session/${examId}`);
const rs = res?.data?.exam?.remaining_seconds;


      if (Number.isFinite(rs) || Number.isInteger(rs)) {
        applyServerRemaining(rs);
        return;
      }

      // If backend returns exam object instead
      const maybeExamRemaining = res?.data?.exam?.remaining_seconds;
      if (Number.isFinite(maybeExamRemaining) || Number.isInteger(maybeExamRemaining)) {
        applyServerRemaining(maybeExamRemaining);
      }
    } catch (e) {
      // If status endpoint doesn't exist, keep running with derived ticking.
      // (No crash, no infinite loop)
    }
  }, [api, examId, applyServerRemaining]);

 const startServerAuthoritativeTimer = useCallback(() => {
  if (!exam) return;
  if (pausedRef.current) return;
  if (submittedRef.current) return;

  // ✅ SEED ONLY ONCE
  if (!seededRef.current) {
    const seed = computeStartSeconds(exam);
    applyServerRemaining(seed);
    seededRef.current = true;
  }

  // 🔁 Local derived tick
  clearIntervalSafe(tickRef);
  tickRef.current = setInterval(() => {
    if (pausedRef.current) return;
    if (submittedRef.current || submittingRef.current) return;

    const nowRemain = deriveRemainingNow();
    setRemainingSec(nowRemain);

    if (
      nowRemain <= 0 &&
      !submittedRef.current &&
      !submittingRef.current &&
      typeof onAutoSubmit === "function"
    ) {
      clearIntervalSafe(tickRef);
      onAutoSubmit();
    }
  }, 1000);

  // 🔁 Server resync
  clearIntervalSafe(syncRef);
  syncRef.current = setInterval(syncRemainingFromServer, 10000);
}, [
  exam,
  computeStartSeconds,
  applyServerRemaining,
  deriveRemainingNow,
  onAutoSubmit,
  syncRemainingFromServer,
]);


  /* ====================== PAUSE / RESUME (OPTIONAL BACKEND) ====================== */
  const pauseTimer = useCallback(async () => {
    pausedRef.current = true;

    clearIntervalSafe(tickRef);
    clearIntervalSafe(syncRef);

    try {
      await api.post(`/exam/${examId}/pause`);
    } catch (e) {
      // optional endpoint; ignore
    }
  }, [api, examId]);

const resumeTimer = useCallback(async () => {
  pausedRef.current = false;

  try {
    await api.post(`/exam/${examId}/resume`);
  } catch {}

  // 🔑 ALWAYS re-sync from server
  await syncRemainingFromServer();

  // 🔁 Restart ticking (NO reseed)
  clearIntervalSafe(tickRef);
  clearIntervalSafe(syncRef);

  tickRef.current = setInterval(() => {
    const now = deriveRemainingNow();
    setRemainingSec(now);

    if (now <= 0 && typeof onAutoSubmit === "function") {
      onAutoSubmit();
    }
  }, 1000);

  syncRef.current = setInterval(syncRemainingFromServer, 10000);
}, [api, examId, syncRemainingFromServer, deriveRemainingNow, onAutoSubmit]);


useEffect(() => {
  seededRef.current = false;
}, [examId]);

  /* ====================== START TIMER WHEN EXAM IS READY ====================== */
  useEffect(() => {
    if (!exam || submittedRef.current) return;
    if (pausedRef.current) return;

    startServerAuthoritativeTimer();

    return () => {
      // Do NOT mark paused here; just clear tick/sync
      clearIntervalSafe(tickRef);
      clearIntervalSafe(syncRef);
    };
  }, [exam, startServerAuthoritativeTimer]);

  /* ====================== VISIBILITY RE-SYNC ====================== */
  useEffect(() => {
    const onVisibility = () => {
      if (pausedRef.current) return;
      if (submittedRef.current || submittingRef.current) return;

      if (document.visibilityState === "visible") {
        // Force a server re-sync when user comes back
        syncRemainingFromServer();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", syncRemainingFromServer);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", syncRemainingFromServer);
    };
  }, [syncRemainingFromServer]);

//===============AUTO-PAUSE ON TAB HIDE / APP SWITCH
useEffect(() => {
  const handleVisibility = async () => {
    if (document.visibilityState === "hidden") {
      if (!pausedRef.current && !submittedRef.current) {
        autoPausedRef.current = true;
        await pauseTimer();
      }
    }

    if (document.visibilityState === "visible") {
      if (autoPausedRef.current && pausedRef.current) {
        autoPausedRef.current = false;
        await resumeTimer();
      }
    }
  };

  document.addEventListener("visibilitychange", handleVisibility);
  return () =>
    document.removeEventListener("visibilitychange", handleVisibility);
}, [pauseTimer, resumeTimer]);

  /* =================================  AUTO-PAUSE ON WINDOW BLUR (DESKTOP)===== */
  
  useEffect(() => {
  const onBlur = async () => {
    if (!pausedRef.current && !submittedRef.current) {
      autoPausedRef.current = true;
      await pauseTimer();
    }
  };

  const onFocus = async () => {
    if (autoPausedRef.current && pausedRef.current) {
      autoPausedRef.current = false;
      await resumeTimer();
    }
  };

  window.addEventListener("blur", onBlur);
  window.addEventListener("focus", onFocus);

  return () => {
    window.removeEventListener("blur", onBlur);
    window.removeEventListener("focus", onFocus);
  };
}, [pauseTimer, resumeTimer]);

    /* =================================  AUTO-PAUSE ON BROWSER CLOSE / REFRESH===== */
	useEffect(() => {
  const beforeUnload = () => {
    if (!submittedRef.current && !pausedRef.current) {
      pauseTimer(); // fire-and-forget
    }
  };

  window.addEventListener("beforeunload", beforeUnload);
  return () =>
    window.removeEventListener("beforeunload", beforeUnload);
}, [pauseTimer]);

	



  /* ====================== DIRTY FLUSH (LOSSLESS) ====================== */
  const flushQuestionIfDirty = useCallback(
    async (questionId) => {
      if (!questionId) return;
      if (submittedRef.current || submittingRef.current) return;

      const dirty = dirtyMapRef.current.get(questionId);
      if (!dirty) return;

      const last = lastSavedRef.current.get(questionId);
      const sameAsLast =
        last &&
        (last.selected_option ?? null) === (dirty.selected_option ?? null) &&
        !!last.flagged === !!dirty.flagged;

      if (sameAsLast) {
        dirtyMapRef.current.delete(questionId);
        return;
      }

      if (inflightSaveRef.current.has(questionId)) return;
      inflightSaveRef.current.add(questionId);

      try {
        // Send derived remainingSec (display) for compatibility;
        // backend should NOT trust client for authority, but this avoids breaking older handlers.
        const safeRemain = deriveRemainingNow();

        await api.patch(`/exam/${examId}/answer`, {
          question_id: questionId,
          selected_option: dirty.selected_option ?? null,
          flagged: !!dirty.flagged,
          remaining_seconds: safeRemain,
        });

        lastSavedRef.current.set(questionId, {
          selected_option: dirty.selected_option ?? null,
          flagged: !!dirty.flagged,
        });

        const stillDirty = dirtyMapRef.current.get(questionId);
        if (
          stillDirty &&
          (stillDirty.selected_option ?? null) === (dirty.selected_option ?? null) &&
          !!stillDirty.flagged === !!dirty.flagged
        ) {
          dirtyMapRef.current.delete(questionId);
        }
      } catch (e) {
        // Silent fail; autosave tick will retry.
      } finally {
        inflightSaveRef.current.delete(questionId);
      }
    },
    [api, examId, deriveRemainingNow]
  );

  /* ====================== NAV SAFE (LOSSLESS) ====================== */
  const setIndexSafely = useCallback(
    async (nextIndex) => {
      if (submittedRef.current || submittingRef.current) return;

      setNavBusy(true);
      setSaving(true);

      try {
        const prevQid = questions[currentIndexRef.current]?.id;
        if (prevQid) await flushQuestionIfDirty(prevQid);
        setCurrentIndex(nextIndex);
      } finally {
        setSaving(false);
        setNavBusy(false);
      }
    },
    [questions, flushQuestionIfDirty]
  );

  /* ====================== ANSWER SELECT (LOSSLESS) ====================== */
  const handleSelectOption = useCallback(
    async (opt) => {
      if (!currentQ || submittedRef.current || submittingRef.current) return;
      const qid = currentQ.id;

      dirtyMapRef.current.set(qid, {
        selected_option: opt,
        flagged: !!flags[qid],
      });

      setAnswers((p) => ({ ...p, [qid]: opt }));

      if (inflightSaveRef.current.has(qid)) return;
      inflightSaveRef.current.add(qid);

      const safeRemain = deriveRemainingNow();

      api
        .patch(`/exam/${examId}/answer`, {
          question_id: qid,
          selected_option: opt,
          flagged: flags[qid] || false,
          remaining_seconds: safeRemain,
        })
        .then(() => {
          lastSavedRef.current.set(qid, {
            selected_option: opt,
            flagged: !!flags[qid],
          });
        })
        .catch(() => {})
        .finally(() => {
          inflightSaveRef.current.delete(qid);
        });
    },
    [api, examId, currentQ, flags, deriveRemainingNow]
  );

  /* ====================== FLAG TOGGLE (LOSSLESS) ====================== */
  const toggleFlag = useCallback(async () => {
    if (!currentQ || submittedRef.current || submittingRef.current) return;

    const qid = currentQ.id;
    const nf = !flags[qid];

    dirtyMapRef.current.set(qid, {
      selected_option: answers[qid] || null,
      flagged: nf,
    });

    setFlags((p) => ({ ...p, [qid]: nf }));

    setSaving(true);

    const safeRemain = deriveRemainingNow();

    try {
      await api.patch(`/exam/${examId}/answer`, {
        question_id: qid,
        selected_option: answers[qid] || null,
        flagged: nf,
        remaining_seconds: safeRemain,
      });

      lastSavedRef.current.set(qid, {
        selected_option: answers[qid] || null,
        flagged: nf,
      });
    } catch (err) {
      // rollback
      setFlags((p) => ({ ...p, [qid]: !nf }));
    } finally {
      setSaving(false);
    }
  }, [api, examId, currentQ, answers, flags, deriveRemainingNow]);

  /* ====================== HEARTBEAT (LOSSLESS) ====================== */
  useEffect(() => {
    if (!exam || submittedRef.current) return;

    clearIntervalSafe(heartbeatRef);
    heartbeatRef.current = setInterval(() => {
      api.post(`/exam/heartbeat/${examId}`).catch(() => {});
    }, 15000);

    return () => clearIntervalSafe(heartbeatRef);
  }, [api, exam, examId]);

  /* ====================== AUTOSAVE (LOSSLESS) ====================== */
  useEffect(() => {
    if (!exam || submittedRef.current) return;

    clearIntervalSafe(autosaveRef);
    autosaveRef.current = setInterval(() => {
      const qid = currentQIdRef.current;
      if (qid) flushQuestionIfDirty(qid);
    }, 10000);

    return () => clearIntervalSafe(autosaveRef);
  }, [exam, flushQuestionIfDirty]);

  /* ====================== EXPOSED API ====================== */
  return {
    // state
    exam,
    questions,
    answers,
    flags,
    currentIndex,
    currentQ,
    remainingSec,
    saving,
    navBusy,
    submittedUI,
    submittingUI,

    // timer controls
    pauseTimer,


    // setters (parent hydrateSession/loadSession will use these)
    setExam,
    setQuestions,
    setAnswers,
    setFlags,
    setCurrentIndex,
    setRemainingSec,
    setSaving,
    setNavBusy,
    setSubmittedUI,
    setSubmittingUI,

    // refs (parent handleSubmit/loadSession will use these)
    timerRef,
    autosaveRef,
    heartbeatRef,
    submittedRef,
    submittingRef,
    dirtyMapRef,
    lastSavedRef,
    inflightSaveRef,
    currentIndexRef,
    currentQIdRef,

    // functions
    computeStartSeconds,
    stopAllIntervals,
    flushQuestionIfDirty,
    setIndexSafely,
    handleSelectOption,
    toggleFlag,
  };
}
